'use client';

import { useState, useEffect, useRef, useMemo, type FormEvent, type ChangeEvent } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Search, MapPin, Building2 } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';
import type { HeroStatItem } from '../types/hotels.types';
import {
  HERO_STATS,
  POPULAR_LOCATIONS,
  TRENDING_DESTINATIONS,
} from './hotels.data';
import { usePublicHotels } from '../hooks/useHotels';

/* -------------------------------------------------------------------------- */
/*  StatCounter                                                               */
/* -------------------------------------------------------------------------- */

function StatCounter({ stat, id }: { stat: HeroStatItem; id: string }) {
  const Icon = stat.icon;
  const display = stat.decimals
    ? stat.value.toFixed(stat.decimals)
    : String(stat.value);

  return (
    <div id={id} className="flex items-center gap-3 px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hms-gold/10">
        <Icon className="h-5 w-5 text-hms-gold" />
      </div>
      <div>
        <p className="text-lg font-bold text-hms-text tracking-tight">
          {display}{stat.suffix}
        </p>
        <p className="text-[11px] text-hms-textDim font-medium uppercase tracking-wider">
          {stat.label}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  HeroSection                                                               */
/* -------------------------------------------------------------------------- */

interface Suggestion {
  type: 'city' | 'hotel';
  label: string;
  slug: string;
  sub?: string;
}

export function HeroSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const { data: dynamicHotels } = usePublicHotels();
  const [destination, setDestination] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  /* Mark form as hydrated for E2E reliability */
  useEffect(() => {
    if (formRef.current) formRef.current.dataset.ready = 'true';
  }, []);

  /* Close suggestions on click-outside */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Compute suggestions from locations + hotels */
  const suggestions = useMemo((): Suggestion[] => {
    const term = destination.trim().toLowerCase();
    if (!term) return [];
    const cityMatches = POPULAR_LOCATIONS
      .filter((loc) => loc.name.toLowerCase().includes(term))
      .slice(0, 4)
      .map((loc) => ({ type: 'city' as const, label: loc.name, slug: loc.slug }));
    const hotels = dynamicHotels ?? [];
    const hotelMatches = hotels
      .filter((h) => h.name.toLowerCase().includes(term) || h.location.toLowerCase().includes(term))
      .slice(0, 3)
      .map((h) => ({ type: 'hotel' as const, label: h.name, slug: h.id, sub: h.location }));
    return [...cityMatches, ...hotelMatches];
  }, [destination, dynamicHotels]);

  const handleSelectSuggestion = (s: Suggestion) => {
    setDestination(s.label);
    setShowSuggestions(false);
    if (s.type === 'hotel') {
      router.push(`/hotels/${s.slug}`);
    } else {
      const params = new URLSearchParams({ q: s.label });
      router.push(`/hotels?${params.toString()}`);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowSuggestions(false);
    const params = new URLSearchParams({
      ...(destination && { q: destination }),
    });
    router.push(`/hotels?${params.toString()}`);
  };

  return (
    <section className="relative w-full overflow-hidden">
      {/* ---- Background layers ---- */}
      <div className="absolute inset-0 bg-hms-bg" />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(13,158,153,0.05) 0%, transparent 70%)',
        }}
      />

      {/* ---- Content ---- */}
      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-24 sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <h1
            id="hotels-hero-title"
            className={cn(
              'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
            )}
          >
            {t('heroSection.title')}
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
            {t('heroSection.subtitle')}
          </p>
        </div>

        {/* ---- Glass-morphism search bar ---- */}
        <form
          ref={formRef}
          id="hotels-hero-search-form"
          action="/hotels"
          onSubmit={handleSubmit}
          className={cn(
            'mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10',
            'bg-white/5 p-3 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-4',
          )}
        >
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div ref={suggestionsRef} className="relative flex-1 min-w-0">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-hms-textDim" />
              <input
                id="hotels-hero-destination"
                name="q"
                type="text"
                autoComplete="off"
                value={destination}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setDestination(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => { if (destination.trim()) setShowSuggestions(true); }}
                placeholder={t('heroSection.searchPlaceholder')}
                className={cn(
                  'h-12 w-full rounded-xl border border-hms-border bg-hms-surface pl-11 pr-3',
                  'text-sm text-hms-text placeholder:text-hms-textDim',
                  'outline-none transition-colors focus:border-hms-gold/50 focus:ring-1 focus:ring-hms-gold/30',
                )}
              />

              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  id="hotels-hero-suggestions"
                  className={cn(
                    'absolute left-0 right-0 top-[calc(100%+4px)] z-50',
                    'max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-hms-border bg-hms-card',
                    'shadow-xl shadow-black/30',
                  )}
                >
                  {suggestions.map((s) => (
                    <button
                      key={`${s.type}-${s.slug}`}
                      id={`hotels-hero-suggestion-${s.slug}`}
                      type="button"
                      onClick={() => handleSelectSuggestion(s)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2.5 text-left',
                        'transition-colors hover:bg-white/5',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                          s.type === 'city'
                            ? 'bg-hms-teal/10 text-hms-teal'
                            : 'bg-hms-gold/10 text-hms-gold',
                        )}
                      >
                        {s.type === 'city' ? (
                          <MapPin className="h-4 w-4" />
                        ) : (
                          <Building2 className="h-4 w-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-hms-text">
                          {s.label}
                        </p>
                        {s.sub && (
                          <p className="truncate text-[11px] text-hms-textDim">
                            {s.sub}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 rounded-md bg-white/5 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-hms-textDim">
                        {s.type === 'city' ? t('heroSection.city') : t('heroSection.hotel')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              id="hotels-hero-search-btn"
              type="submit"
              className={cn(
                'inline-flex h-12 shrink-0 items-center gap-2 rounded-xl px-7',
                'bg-hms-gold font-semibold text-black text-sm',
                'shadow-lg shadow-hms-gold/20 transition-all',
                'hover:bg-hms-goldLt hover:shadow-hms-gold/30',
                'active:scale-[0.98]',
              )}
            >
              <Search className="h-4 w-4" />
              {t('heroSection.search')}
            </button>
          </div>
        </form>

        {/* ---- Trending destination chips ---- */}
        <div id="hotels-hero-trending" className="mx-auto mt-5 flex max-w-2xl flex-wrap items-center justify-center gap-2">
          <span className="text-[11px] font-medium text-hms-textDim">{t('heroSection.trending')}</span>
          {TRENDING_DESTINATIONS.map((dest) => (
            <Link
              key={dest.slug}
              id={`hotels-hero-trend-${dest.slug}`}
              href={`/hotels?q=${encodeURIComponent(dest.name)}`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border border-hms-gold/20 px-3 py-1.5',
                'text-[11px] font-medium text-hms-gold',
                'transition-all hover:border-hms-gold/40 hover:bg-hms-gold/5',
              )}
            >
              <MapPin className="h-3 w-3" />
              {dest.name}
            </Link>
          ))}
        </div>

        {/* ---- Stats bar ---- */}
        <div className="mx-auto mt-16 max-w-4xl">
          <p id="hotels-hero-stats-label" className="mb-4 text-center text-xs font-medium uppercase tracking-widest text-hms-textDim">
            {t('heroSection.trustedBy')}
          </p>
          <div
            className={cn(
              'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hms-border bg-hms-border md:grid-cols-4',
            )}
          >
            {HERO_STATS.map((stat, index) => (
              <div key={stat.label} className="bg-hms-card">
                <StatCounter stat={stat} id={`hotels-hero-stat-${index}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
